/* eslint-disable react/prop-types */
/**
 * AlignmentRecommendationModal
 * ============================
 * iter184 — fast structured wheel-alignment recommendation builder.
 *
 * Target: FOH adds a fully explained alignment recommendation in
 * under 10 seconds.
 *
 * Three-section single-screen layout (NO multi-step wizard — the
 * user spec explicitly asked for "ultra-fast intelligent modal"):
 *
 *   1. Alignment type radio (5 options, prices visible)
 *   2. Trigger reasons multi-select chip grid (13 reasons)
 *   3. Live 4-stage explanation preview
 *
 * Save → POST /api/foh/yestimate/{yid}/recommendations/alignment.
 * Cancel → close, no side effect.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle2, AlertTriangle, Loader2, MoveRight,
} from "lucide-react";

const DEFAULT_TYPE = "four_wheel";

export default function AlignmentRecommendationModal({
  open,
  onOpenChange,
  yestimateId,
  initial = null,            // existing recommendation when editing
  suggestionContext = null,  // {active_triggers, recommended_alignment_type, linked_jg_ids}
  jobGroups = [],            // for "linked repairs" picker
  onSaved,                   // (rec) => void
}) {
  const [catalog, setCatalog] = useState(null);
  const [alignmentType, setAlignmentType] = useState(
    initial?.alignment_type || suggestionContext?.recommended_alignment_type || DEFAULT_TYPE,
  );
  const [triggers, setTriggers] = useState(
    initial?.trigger_reasons
      || suggestionContext?.active_triggers
      || [],
  );
  const [priceOverride, setPriceOverride] = useState(
    initial?.price_override ?? "",
  );
  const [linkedRepairs, setLinkedRepairs] = useState(
    initial?.linked_repair_jg_ids
      || suggestionContext?.linked_jg_ids
      || [],
  );
  const [customNote, setCustomNote] = useState(initial?.notes || "");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load catalog once.
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/recommendations/catalog`)
      .then((r) => {
        const align = (r.data?.recommendation_types || [])
          .find((t) => t.subtype === "wheel_alignment");
        setCatalog(align || null);
      })
      .catch(() => setCatalog(null));
  }, [open]);

  // Live preview on selection change.
  useEffect(() => {
    if (!open || !yestimateId) return;
    const ctrl = new AbortController();
    const run = async () => {
      try {
        const r = await axios.post(
          `${API}/foh/yestimate/${yestimateId}/recommendations/alignment/preview`,
          {
            alignment_type: alignmentType,
            trigger_reasons: triggers,
            price_override: priceOverride === "" ? null : Number(priceOverride),
            linked_repair_jg_ids: linkedRepairs,
            custom_note: customNote || null,
          },
          { signal: ctrl.signal },
        );
        setPreview(r.data);
      } catch (e) {
        if (axios.isCancel(e)) return;
        setPreview(null);
      }
    };
    run();
    return () => ctrl.abort();
  }, [open, yestimateId, alignmentType, triggers, priceOverride, linkedRepairs, customNote]);

  const alignmentOptions = catalog?.alignment_types || [];
  const triggerOptions = catalog?.trigger_reasons || [];

  const toggleTrigger = (key) => {
    setTriggers((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const isIncluded = alignmentType === "included_with_repairs";
  const linkableRepairs = useMemo(
    () => (jobGroups || []).filter(
      (jg) => jg.subtype !== "wheel_alignment" && jg.id,
    ),
    [jobGroups],
  );

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const r = await axios.post(
        `${API}/foh/yestimate/${yestimateId}/recommendations/alignment`,
        {
          alignment_type:       alignmentType,
          trigger_reasons:      triggers,
          price_override:       priceOverride === "" ? null : Number(priceOverride),
          linked_repair_jg_ids: linkedRepairs,
          custom_note:          customNote || null,
        },
      );
      onSaved?.(r.data?.recommendation);
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save the recommendation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="alignment-recommendation-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MoveRight className="w-5 h-5 text-emerald-600" />
            {initial ? "Edit" : "Add"} Wheel Alignment Recommendation
          </DialogTitle>
          <p className="text-[12.5px] text-slate-500 italic mt-0.5">
            Geometry correction recommendation — explanation auto-generates from your choices.
          </p>
        </DialogHeader>

        {/* Step 1 — alignment type */}
        <section className="space-y-2" data-testid="alignment-type-section">
          <Label className="text-[12px] uppercase tracking-wide text-slate-600 font-semibold">
            Alignment type
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            {alignmentOptions.map((opt) => {
              const selected = opt.key === alignmentType;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setAlignmentType(opt.key)}
                  className={`text-left rounded-lg border-2 px-3 py-2 transition-colors ${
                    selected
                      ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300"
                      : "border-slate-200 bg-white hover:border-slate-400"
                  }`}
                  data-testid={`alignment-type-${opt.key}`}
                  data-selected={selected}
                >
                  <p className="text-[12.5px] font-bold text-slate-900 leading-snug">
                    {opt.short_label}
                  </p>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    £{Number(opt.default_price).toFixed(0)} · {opt.default_hours}h
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 2 — triggers */}
        <section className="space-y-2" data-testid="trigger-reasons-section">
          <Label className="text-[12px] uppercase tracking-wide text-slate-600 font-semibold">
            Reason(s) for recommendation — pick all that apply
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {triggerOptions.map((opt) => {
              const on = triggers.includes(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => toggleTrigger(opt.key)}
                  className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${
                    on
                      ? "bg-emerald-600 text-white border-emerald-700"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                  }`}
                  data-testid={`trigger-chip-${opt.key}`}
                  data-selected={on}
                >
                  {on ? "✓ " : ""}{opt.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Optional — linked repairs (only when "included with repairs") */}
        {isIncluded && linkableRepairs.length > 0 && (
          <section className="space-y-2 bg-blue-50/50 border border-blue-200 rounded-lg p-3">
            <Label className="text-[12px] uppercase tracking-wide text-blue-700 font-semibold">
              Repairs that include this alignment
            </Label>
            <p className="text-[11.5px] text-blue-800 italic">
              Selected repairs will show an "Includes wheel alignment" badge on the portal.
            </p>
            <div className="space-y-1">
              {linkableRepairs.map((jg) => (
                <label
                  key={jg.id}
                  className="flex items-center gap-2 py-1 text-[12.5px] text-slate-800"
                >
                  <input
                    type="checkbox"
                    checked={linkedRepairs.includes(jg.id)}
                    onChange={() =>
                      setLinkedRepairs((prev) =>
                        prev.includes(jg.id)
                          ? prev.filter((x) => x !== jg.id)
                          : [...prev, jg.id],
                      )
                    }
                  />
                  <span>{jg.title || jg.label}</span>
                </label>
              ))}
            </div>
          </section>
        )}

        {/* Optional — price override + custom note */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[12px] uppercase tracking-wide text-slate-600 font-semibold">
              Price override
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-slate-500">£</span>
              <Input
                type="number"
                step="0.01"
                placeholder={`Default ${Number(
                  (alignmentOptions.find((o) => o.key === alignmentType) || {}).default_price || 0,
                ).toFixed(2)}`}
                value={priceOverride}
                onChange={(e) => setPriceOverride(e.target.value)}
                className="text-[13px]"
                data-testid="alignment-price-override"
              />
            </div>
          </div>
          <div>
            <Label className="text-[12px] uppercase tracking-wide text-slate-600 font-semibold">
              Custom note (optional, secondary)
            </Label>
            <Textarea
              value={customNote}
              onChange={(e) => setCustomNote(e.target.value)}
              rows={2}
              placeholder="Anything additional FOH wants the tester / mechanic to see — does NOT replace the structured explanation."
              className="text-[13px] mt-1"
              data-testid="alignment-custom-note"
            />
          </div>
        </section>

        {/* Live preview — 4-stage */}
        <section
          className="rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-3"
          data-testid="alignment-preview"
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <Label className="text-[12px] uppercase tracking-wide text-emerald-700 font-semibold">
              Customer-facing preview
            </Label>
            {preview?.integrity_tier && (
              <span className="text-[10.5px] uppercase tracking-wider bg-white border border-emerald-300 text-emerald-700 px-1.5 py-0.5 rounded">
                Tier: {preview.integrity_tier.replace("_", " ")}
              </span>
            )}
          </div>
          {!preview ? (
            <p className="text-[12px] text-slate-500 italic">
              Loading preview…
            </p>
          ) : (
            <div className="space-y-2 text-[12.5px] text-slate-800">
              {(preview.reason_badges || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {(preview.reason_badges || []).map((b) => (
                    <span
                      key={b}
                      className="text-[10.5px] uppercase bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-1.5 py-0.5"
                    >
                      {b}
                    </span>
                  ))}
                </div>
              )}
              {["what_found", "what_means", "whats_required", "consequence"].map((k) => {
                const labels = {
                  what_found: "What we found",
                  what_means: "What this means",
                  whats_required: "What's required",
                  consequence: "What happens if not addressed",
                };
                const val = (preview.generated_explanation || {})[k];
                if (!val) return null;
                return (
                  <div key={k} data-testid={`preview-stage-${k}`}>
                    <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                      {labels[k]}
                    </p>
                    <p className="text-[12.5px] leading-snug">{val}</p>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {error && (
          <div
            className="text-[12.5px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-start gap-2"
            data-testid="alignment-error"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="alignment-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !alignmentType}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="alignment-save"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
            ) : (
              <><CheckCircle2 className="w-4 h-4 mr-2" />Save recommendation</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
